'use client';

import { useState, ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import type { Review } from '@alltrails/types/Review';
import useUser from '@alltrails/context/hooks/useUser';
import ReviewItem from '@alltrails/reviews/components/ReviewItem/ReviewItem';
import Button from '@alltrails/denali/components/Button';
import cardStyles from '../../components/CardModule/CardModule.module.scss';
import { useSelector } from '../../utils/redux';
import styles from './FeaturedReview.module.scss';
import { REVIEWS_SECTION_ID } from '@/utils/constants';

const ReviewFormModal = dynamic(() => import('@alltrails/reviews/components/NewReviewFormModal/NewReviewFormModal'));
const FeaturedReviewModal = dynamic(() => import('./FeaturedReviewModal'));

type FeaturedReviewProps = {
  review: Review;
};

const FeaturedReview = ({ review }: FeaturedReviewProps) => {
  const context = useSelector(state => state.context);
  const user = useUser();
  const [isFeaturedReviewModalOpen, setIsFeaturedReviewModalOpen] = useState(false);
  const [isReviewFormOpen, toggleReviewForm] = useState(false);
  const [reviewFormProps, setReviewFormProps] = useState<ComponentProps<typeof ReviewFormModal>>();
  const [currentTrailId, setCurrentTrailId] = useState<number | undefined>();

  const openReviewFormModal = (formProps: ComponentProps<typeof ReviewFormModal>) => {
    setReviewFormProps(formProps);
    toggleReviewForm(true);
    setCurrentTrailId(formProps.trailId);
  };

  const onShowAllReviewsClick = () => {
    document.getElementById(REVIEWS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={cardStyles.card}>
      <div className={classNames(cardStyles.inner, styles.featuredReviewContainer)}>
        {/* TODO: Translate header if we roll out this feature */}
        <div>
          <div className={cardStyles.cardTitle}>Featured review</div>
          <ReviewItem
            analyticsReportLocation="trail details"
            context={context}
            includeTrailName={false}
            index={0}
            languageRegionCode={context.languageRegionCode}
            openReviewFormModal={openReviewFormModal}
            review={review}
            photosOverflowOnMobile={false}
            hideBorderBottom
            featuredReviewTruncation
            containerClassName={styles.reviewContainer}
            onShowMoreClick={() => setIsFeaturedReviewModalOpen(true)}
          />
        </div>
        <div className={styles.action}>
          <Button
            title="Show all reviews"
            testId="show-all-reviews-btn"
            text={<FormattedMessage defaultMessage="Show all reviews" />}
            size="md"
            variant="elevated"
            onClick={onShowAllReviewsClick}
          />
        </div>
      </div>
      <FeaturedReviewModal
        isOpen={isFeaturedReviewModalOpen}
        onCloseModal={() => setIsFeaturedReviewModalOpen(false)}
        review={review}
        openReviewFormModal={openReviewFormModal}
      />
      {user && isReviewFormOpen && (
        <ReviewFormModal
          activity={reviewFormProps?.activity}
          associatedRecording={reviewFormProps?.associatedRecording}
          associatedTrailReviewId={reviewFormProps?.associatedTrailReviewId}
          closeReviewModal={() => toggleReviewForm(false)}
          comment={reviewFormProps?.comment}
          comment_source={reviewFormProps?.comment_source}
          date={reviewFormProps?.date}
          difficulty={reviewFormProps?.difficulty}
          id={reviewFormProps?.id}
          infoAttributes={reviewFormProps?.infoAttributes}
          isActivity={reviewFormProps?.isActivity}
          obstacles={reviewFormProps?.obstacles}
          rating={reviewFormProps?.rating}
          ratingAttributes={reviewFormProps?.ratingAttributes}
          reviewSource={reviewFormProps?.reviewSource}
          trailId={currentTrailId}
          user={user}
        />
      )}
    </div>
  );
};

export default FeaturedReview;
